import {
  ListItemButton,
  ListItemText,
  Paper,
  SvgIcon,
} from '@ahamove/web-components';
import ListItem from '@ahamove/web-components/lib/ListItem';
import React, { useEffect } from 'react';
import type { Address, HubData } from '@/api/place/types';
import { useGetHubs } from '@/api/place/useGetHubs';
import type { AvailableCity } from '@/api/service/useGetAvailableCities';
import { useGetAvailableCities } from '@/api/service/useGetAvailableCities';
import { useCurrentLocationStore } from '@/global-stores/useCurrentLocationStore';
import { useLocationStore } from '@/global-stores/useLocationStore';
import { useRentTypeStore } from '@/global-stores/useRentTypeStore';
import { ChevronRightGrayIcon } from '@/lib/icons';
import { hubToAddress } from '@/lib/utils';
import EmptyLocation from './EmptyLocation';
import ErrorCity from './ErrorCity';
import LocationItem from './LocationItem';
import LocationItemLoading from './LocationItemLoading';

type Props = {
  onSelectHub: (address: Address) => void;
  handleNextStepPickHub: (value: string) => void;
  isResetListHub: boolean;
};

export default function PickupAtHub({
  onSelectHub,
  handleNextStepPickHub,
  isResetListHub = false,
}: Props) {
  const { currentAddress } = useCurrentLocationStore();
  const { rentType } = useRentTypeStore();
  const { address } = useLocationStore();
  const { isHub, setCityId, cityId, setCity } = useLocationStore();

  const {
    data: listHubs,
    isFetching: loadingHubs,
    isError,
  } = useGetHubs(
    cityId
      ? { city_id: cityId }
      : address
      ? {
          lng: address?.lng as number,
          lat: address?.lat as number,
        }
      : {
          lng: currentAddress?.lng as number,
          lat: currentAddress?.lat as number,
        },

    {
      enabled: !!currentAddress || !!cityId || (!!address && !isHub),
    },
    rentType
  );

  useEffect(() => {
    if (listHubs?.length) {
      setCity(listHubs[0]?.city_id);
    }
  }, [listHubs]);

  const { data: availableCities } = useGetAvailableCities(
    { service_type: rentType },
    {
      enabled: !currentAddress,
    }
  );

  const showListHub = (item: AvailableCity) => {
    setCityId(item._id);
    setCity(item._id);
    handleNextStepPickHub(item.name_vi_vn);
  };

  const isExistDataListHub = React.useMemo(() => {
    return listHubs && listHubs.length > 0;
  }, [listHubs]);

  React.useEffect(() => {
    if (!isResetListHub) return;
    setCityId('');
  }, [isResetListHub]);

  if (loadingHubs)
    return (
      <>
        {Array.from(Array(3).keys()).map((item, index) => {
          return <LocationItemLoading divider={item < 2} key={index} />;
        })}
      </>
    );

  if (isError) return <ErrorCity />;

  if (
    (!isExistDataListHub || (isExistDataListHub && isHub)) &&
    !cityId &&
    availableCities &&
    availableCities.length > 0
  )
    return (
      // <ListItem>
      //   <Stack direction="column" alignItems="center" px={5} py={7.5}>
      //     <GPSIcon />
      //     <Typography variant="h6" color="grey.600" align="center" mt={2.25}>
      //       {t('booking:location.please_allow_location_desc')}
      //     </Typography>
      //   </Stack>
      // </ListItem>

      <>
        {availableCities.map((item: AvailableCity, index: number) => (
          <Paper
            key={index}
            sx={{
              boxShadow: '0px 10px 24px -5px rgba(15, 15, 15, 0.08)',
              borderRadius: 3,
              mt: 1.5,
              mx: 2,
              height: 64,
              border: '1px solid #F2F4F8',
              mb: index === availableCities.length - 1 ? 2 : 0,
            }}
          >
            <ListItemButton
              onClick={() => showListHub(item)}
              sx={{
                m: 0,
                px: 2.75,
                py: 2,
                pr: 0.75,
                height: '100%',
                borderRadius: 3,
              }}
              disableRipple
            >
              <ListItem
                divider
                secondaryAction={
                  <SvgIcon inheritViewBox component={ChevronRightGrayIcon} />
                }
                sx={{ border: 'none', p: 0 }}
              >
                <ListItemText
                  sx={{
                    span: { fontSize: 20, fontWeight: 700, lineHeight: 1.6 },
                  }}
                  primary={item.name_vi_vn}
                />
              </ListItem>
            </ListItemButton>
          </Paper>
        ))}
      </>
    );

  if (!listHubs || (listHubs && listHubs.length <= 0)) {
    return <EmptyLocation type="location" />;
  }

  return (
    <>
      {listHubs.map((hub: HubData, index: number) => {
        const addr = hubToAddress(hub);

        return (
          <LocationItem
            key={index}
            address={addr}
            onClick={() => onSelectHub(addr)}
            divider={index < listHubs.length - 1}
          />
        );
      })}
    </>
  );
}
